"use client";

import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { DEFAULT_FAQ, resolveFaq } from "@/lib/site-copy";
import { DEFAULT_DELIVERY_FEE, DEFAULT_FREE_DELIVERY_AT } from "@/lib/shipping";
import type { FaqItem } from "@/types";

const contactSchema = z.object({
  name: z.string().min(2, "이름을 2자 이상 입력해주세요"),
  email: z.string().email("올바른 이메일을 입력해주세요"),
  subject: z.string().min(2, "제목을 입력해주세요"),
  message: z.string().min(10, "문의 내용을 10자 이상 입력해주세요"),
});

type ContactFormValues = z.infer<typeof contactSchema>;

interface SiteInfo {
  phone: string | null;
  email: string | null;
  bankName: string | null;
  bankAccount: string | null;
  bankHolder: string | null;
  // 판매 중인 상품 전체 기준 공통값. 상품마다 달라 하나로 말할 수 없으면 null.
  deliveryFee: number | null;
  freeDeliveryAt: number | null;
}

const DEFAULT_INFO: SiteInfo = {
  phone: "010-0000-0000",
  email: "contact@hyundeulfarm.com",
  bankName: "농협은행",
  bankAccount: "000-0000-0000",
  bankHolder: "현들팜",
  deliveryFee: DEFAULT_DELIVERY_FEE,
  freeDeliveryAt: DEFAULT_FREE_DELIVERY_AT,
};

const VARIES_BY_PRODUCT_LABEL = "상품별로 다름 (상품 상세페이지 참고)";

// /api/settings의 commonDeliveryFee/commonFreeDeliveryAt은 값(공통) 또는
// null(상품마다 다름)이다. 숫자도 null도 아니면(요청 실패 등) 기본값으로.
function parseCommonShippingValue(
  value: unknown,
  fallback: number,
): number | null {
  if (typeof value === "number") {
    return value;
  }

  if (value === null) {
    return null;
  }

  return fallback;
}

function describeDeliveryFee(deliveryFee: number | null): string {
  if (deliveryFee === null) {
    return VARIES_BY_PRODUCT_LABEL;
  }

  if (deliveryFee === 0) {
    return "무료";
  }

  return `${deliveryFee.toLocaleString("ko-KR")}원`;
}

// freeDeliveryAt 0 = 0원 이상이므로 항상 무료배송(TrustBar와 동일한 표현).
function describeFreeDeliveryAt(freeDeliveryAt: number | null): string {
  if (freeDeliveryAt === null) {
    return VARIES_BY_PRODUCT_LABEL;
  }

  if (freeDeliveryAt === 0) {
    return "모든 상품 무료배송";
  }

  return `${freeDeliveryAt.toLocaleString("ko-KR")}원 이상 구매 시`;
}

function FaqAccordion({ items }: { items: FaqItem[] }) {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  return (
    <div className="space-y-3">
      {items.map((item, i) => (
        <div key={i} className="card overflow-hidden">
          <button
            type="button"
            className="w-full flex items-center justify-between p-5 text-left"
            onClick={() => setOpenIndex(openIndex === i ? null : i)}
            aria-expanded={openIndex === i}
          >
            <span className="font-semibold text-[var(--color-text-dark)] pr-4">
              {item.question}
            </span>
            <span
              className={[
                "flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-full transition-transform text-[var(--color-primary)] border-2 border-[var(--color-primary)]",
                openIndex === i ? "rotate-180" : "",
              ].join(" ")}
              aria-hidden
            >
              ↓
            </span>
          </button>
          {openIndex === i && (
            <div className="px-5 pb-5 text-[var(--color-text-medium)] leading-relaxed text-sm border-t border-[var(--color-border)] pt-4 whitespace-pre-line">
              {item.answer}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

export default function ContactPage() {
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState<SiteInfo>(DEFAULT_INFO);
  const [faq, setFaq] = useState<FaqItem[]>(DEFAULT_FAQ);

  useEffect(() => {
    fetch("/api/settings")
      .then((r) => r.json())
      .then((json) => {
        if (json.success && json.data) {
          setInfo({
            phone: json.data.phone || DEFAULT_INFO.phone,
            email: json.data.email || DEFAULT_INFO.email,
            bankName: json.data.bankName || DEFAULT_INFO.bankName,
            bankAccount: json.data.bankAccount || DEFAULT_INFO.bankAccount,
            bankHolder: json.data.bankHolder || DEFAULT_INFO.bankHolder,
            deliveryFee: parseCommonShippingValue(
              json.data.commonDeliveryFee,
              DEFAULT_DELIVERY_FEE,
            ),
            freeDeliveryAt: parseCommonShippingValue(
              json.data.commonFreeDeliveryAt,
              DEFAULT_FREE_DELIVERY_AT,
            ),
          });
          setFaq(resolveFaq(json.data.faqItems));
        }
      })
      .catch(() => {});
  }, []);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ContactFormValues>({
    resolver: zodResolver(contactSchema),
  });

  async function onSubmit(values: ContactFormValues) {
    setSubmitting(true);
    setError("");
    try {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(values),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error ?? "전송 실패");
      setSent(true);
      reset();
    } catch (err: unknown) {
      setError(
        err instanceof Error ? err.message : "전송 중 오류가 발생했습니다.",
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="container section-padding space-y-16">
      <div className="section-title">
        <h1 className="text-3xl md:text-4xl">고객센터</h1>
        <span className="gold-line" />
        <p>궁금하신 점이 있으시면 언제든지 문의해주세요.</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
        <div className="card p-6 text-center space-y-3">
          <span className="text-4xl" aria-hidden>
            📞
          </span>
          <h3 className="font-bold text-[var(--color-primary)]">전화 문의</h3>
          <p className="text-lg font-bold text-[var(--color-text-dark)]">
            {info.phone}
          </p>
          <p className="text-xs text-[var(--color-text-muted)]">
            평일 09:00 ~ 18:00
          </p>
        </div>

        <div className="card p-6 text-center space-y-3">
          <span className="text-4xl" aria-hidden>
            🏦
          </span>
          <h3 className="font-bold text-[var(--color-primary)]">계좌이체</h3>
          <p className="font-medium text-[var(--color-text-dark)] text-sm">
            {info.bankName}
          </p>
          <p className="text-lg font-bold text-[var(--color-primary)]">
            {info.bankAccount}
          </p>
          <p className="text-sm text-[var(--color-text-muted)]">
            예금주: {info.bankHolder}
          </p>
        </div>

        <div className="card p-6 text-center space-y-3">
          <span className="text-4xl" aria-hidden>
            📧
          </span>
          <h3 className="font-bold text-[var(--color-primary)]">이메일 문의</h3>
          <p className="text-sm text-[var(--color-text-medium)]">
            {info.email}
          </p>
          <p className="text-xs text-[var(--color-text-muted)]">
            24시간 이내 답변
          </p>
        </div>
      </div>

      <section className="card p-8 space-y-4">
        <h2 className="text-xl font-bold text-[var(--color-primary)]">
          배송 안내
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm text-[var(--color-text-medium)]">
          {[
            { label: "배송 방법", value: "우체국택배" },
            { label: "배송 기간", value: "주문 확인 후 2~3일(영업일 기준)" },
            {
              label: "기본 배송비",
              value: describeDeliveryFee(info.deliveryFee),
            },
            {
              label: "기본 무료 배송",
              value: describeFreeDeliveryAt(info.freeDeliveryAt),
            },
            { label: "배송 불가", value: "도서·산간 지역 일부 추가 요금 발생" },
            { label: "공지", value: "농번기·명절 전후 배송 지연 가능" },
          ].map((item) => (
            <div key={item.label} className="flex gap-3">
              <span className="font-semibold text-[var(--color-primary)] w-28 flex-shrink-0">
                {item.label}
              </span>
              <span>{item.value}</span>
            </div>
          ))}
        </div>
      </section>

      <section className="space-y-6">
        <h2 className="text-2xl font-bold text-[var(--color-primary)]">
          자주 묻는 질문
        </h2>
        <FaqAccordion items={faq} />
      </section>

      <section className="space-y-6">
        <h2 className="text-2xl font-bold text-[var(--color-primary)]">
          이메일 문의
        </h2>

        {sent ? (
          <div className="card p-8 text-center space-y-3">
            <span className="text-5xl" aria-hidden>
              ✅
            </span>
            <h3 className="text-xl font-bold text-[var(--color-primary)]">
              문의가 접수되었습니다
            </h3>
            <p className="text-[var(--color-text-light)]">
              24시간 이내에 답변드리겠습니다.
            </p>
            <button
              type="button"
              onClick={() => setSent(false)}
              className="btn-outline mt-2"
            >
              추가 문의하기
            </button>
          </div>
        ) : (
          <form
            onSubmit={handleSubmit(onSubmit)}
            noValidate
            className="card p-8 space-y-5"
          >
            {/* 이름·이메일은 입력값 자체가 개인정보라 세션 리플레이(ph-mask)와
                오토캡처(ph-no-capture) 양쪽에서 제외한다. */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 ph-mask ph-no-capture">
              <div>
                <label className="label" htmlFor="contact-name">
                  이름 <span className="text-[var(--color-error)]">*</span>
                </label>
                <input
                  id="contact-name"
                  className="input"
                  placeholder="홍길동"
                  aria-invalid={!!errors.name}
                  aria-describedby={
                    errors.name ? "contact-name-err" : undefined
                  }
                  {...register("name")}
                />
                {errors.name && (
                  <p
                    id="contact-name-err"
                    role="alert"
                    className="text-xs text-[var(--color-error)] mt-1"
                  >
                    {errors.name.message}
                  </p>
                )}
              </div>

              <div>
                <label className="label" htmlFor="contact-email">
                  이메일 <span className="text-[var(--color-error)]">*</span>
                </label>
                <input
                  id="contact-email"
                  type="email"
                  className="input"
                  placeholder="email@example.com"
                  aria-invalid={!!errors.email}
                  aria-describedby={
                    errors.email ? "contact-email-err" : undefined
                  }
                  {...register("email")}
                />
                {errors.email && (
                  <p
                    id="contact-email-err"
                    role="alert"
                    className="text-xs text-[var(--color-error)] mt-1"
                  >
                    {errors.email.message}
                  </p>
                )}
              </div>
            </div>

            <div className="ph-mask ph-no-capture">
              <label className="label" htmlFor="contact-subject">
                제목 <span className="text-[var(--color-error)]">*</span>
              </label>
              <input
                id="contact-subject"
                className="input"
                placeholder="문의 제목을 입력해주세요"
                aria-invalid={!!errors.subject}
                aria-describedby={
                  errors.subject ? "contact-subject-err" : undefined
                }
                {...register("subject")}
              />
              {errors.subject && (
                <p
                  id="contact-subject-err"
                  role="alert"
                  className="text-xs text-[var(--color-error)] mt-1"
                >
                  {errors.subject.message}
                </p>
              )}
            </div>

            <div className="ph-mask ph-no-capture">
              <label className="label" htmlFor="contact-message">
                문의 내용 <span className="text-[var(--color-error)]">*</span>
              </label>
              <textarea
                id="contact-message"
                rows={6}
                className="input resize-none"
                placeholder="문의 내용을 입력해주세요"
                aria-invalid={!!errors.message}
                aria-describedby={
                  errors.message ? "contact-message-err" : undefined
                }
                {...register("message")}
              />
              {errors.message && (
                <p
                  id="contact-message-err"
                  role="alert"
                  className="text-xs text-[var(--color-error)] mt-1"
                >
                  {errors.message.message}
                </p>
              )}
            </div>

            <div aria-live="polite" aria-atomic="true">
              {error && (
                <div
                  role="alert"
                  className="p-4 bg-[#FEE2E2] rounded-xl text-[var(--color-error)] text-sm"
                >
                  {error}
                </div>
              )}
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="btn-primary w-full"
            >
              {submitting ? "전송 중..." : "문의 보내기"}
            </button>
          </form>
        )}
      </section>
    </div>
  );
}
